<template lang="pug">
.sosSelectWrapper

  label.sosSelectTitle(v-if="!!label")
    span(v-html="label")

  .sosSelectField
    .sosSelect(
      @click="openModal"
      :class="{ 'sosSelect--disabled': disabled, 'sosSelect--showing': dialog }"
      :tabindex="disabled ? -1 : 0"
      @keydown.enter.prevent="openModal"
    )
      .sosSelectSelected {{ selectedText }}

  v-dialog(v-model="dialog" max-width="600px" persistent scrollable)
    div
      .dialogHeader
        .dialogHeaderTitle {{ modalTitle || label || 'Selecionar Usuários' }}
        .dialogHeaderClose
          .dialogCloseButton.mdi.mdi-close(@click="closeModal")

      .dialogBody
        .dialogBodyContent
          .searchBlock.mb-3(v-if="!loading")
            v-icon.searchFieldIcon(size="18") mdi-magnify
            input.searchField(
              type="text"
              v-model="searchText"
              placeholder="Pesquisar por nome ou e-mail"
            )

          v-progress-linear(v-if="loading" indeterminate color="primary")

          .user-list.mt-2(v-if="!loading")
            div(
              v-for="user in selectableGroupUsers"
              :key="user.uid || user.userID"
            )
              scale-user-list-item(
                :source="user"
                :click="onUserClick"
                :isSelected="isSelected(user.userID || user.uid)"
                :isMultiSelectActive="true"
              )

            .empty-state.pa-4(v-if="!loading && selectableGroupUsers.length === 0")
              | Nenhum usuário encontrado.

          nav.userManagerNav.mt-3(v-if="totalPages > 1 && !loading")
            .userManagerNavColumn--button.userManagerNavColumn--textLeft
              a(@click.prevent="previousPage" :class="{ 'current': currentPage === 1 }") &#8249;
            .userManagerNavColumn--pages
              span
                .sosNavigationItem(
                  v-for="(page, pageIndex) in pageNumbers"
                  :key="`upage_${pageIndex}`"
                )
                  i(v-show="page === '...'") {{ '...' }}
                  a(
                    v-show="page !== '...'"
                    @click.prevent="setPage(page)"
                    :class="{ 'current': currentPage === Number(page) }"
                  ) {{ page }}
            .userManagerNavColumn--button.userManagerNavColumn--textRight
              a(@click.prevent="nextPage" :class="{ 'current': currentPage === totalPages }") &#8250;

      .dialogFooter.text-right
        sos-button.mr-3(
          @buttonClick="closeModal"
          size="medium"
          buttonLabel="Cancelar"
          buttonIcon="mdi-close"
          color="grey"
          :disabled="loading"
        )
        sos-button(
          @buttonClick="confirmSelection"
          size="medium"
          buttonLabel="Confirmar"
          buttonIcon="mdi-check"
          color="success"
          :disabled="loading"
        )

</template>

<script>
import { mapState, mapActions } from 'vuex';
import ScaleUserListItem from '@/components/ScaleUserListItem';
import SosButton from '@/components/SosButton';

export default {
  name: 'SosUserSelect',

  components: {
    ScaleUserListItem,
    'sos-button': SosButton,
  },

  props: {
    label: { type: String, required: true },
    groupId: { type: String, default: null },
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    defaultEmpty: { type: String, default: 'Selecione os usuários' },
    modalTitle: { type: String, default: '' },
    singleSelect: { type: Boolean, default: false },
    excludedUserIds: { type: Array, default: () => [] },
  },

  data() {
    return {
      dialog: false,
      loading: false,
      searchText: '',
      currentPage: 1,
      localSelectedIds: [],
      usersCache: {},
      currentSearchRequestId: 0,
    };
  },

  computed: {
    ...mapState({
      // Este componente dispara loadGroupUsers (paginado/backend) — usa a state
      // dedicada groupUsersPaginated, separada de groupUsers (lista completa via
      // Firebase, usada por Acompanhar/Criar Escala). Ver comentário em store/app.js.
      groupUsers: (state) => state.app.groupUsersPaginated,
      totalUsers: (state) => state.app.totalUsersPaginated,
      groupUsersItemsPerPage: (state) => state.app.groupUsersPaginatedItemsPerPage,
    }),

    totalPages() {
      return Math.ceil(this.totalUsers / this.groupUsersItemsPerPage) || 1;
    },

    selectableGroupUsers() {
      const excluded = this.excludedUserIds.map(String);
      return this.groupUsers.filter((user) => !excluded.includes(String(user.userID || user.uid)));
    },

    pageNumbers() {
      const visiblePages = 5;
      const pages = [];
      const startPage = Math.max(1, this.currentPage - Math.floor(visiblePages / 2));
      const endPage = Math.min(this.totalPages, startPage + visiblePages - 1);
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      if (startPage > 1) {
        pages.unshift('...');
        pages.unshift(1);
      }
      if (endPage < this.totalPages) {
        pages.push('...');
        pages.push(this.totalPages);
      }
      return pages;
    },

    selectedText() {
      if (!this.value || this.value.length === 0) return this.defaultEmpty;
      const names = this.value
        .map((id) => (this.usersCache[id] ? this.usersCache[id].userName : null))
        .filter(Boolean);
      if (names.length === this.value.length) {
        return names.length === 1 ? names[0] : `(${names.length}) ${names.join(', ')}`;
      }
      return `(${this.value.length}) usuário${this.value.length !== 1 ? 's' : ''} selecionado${this.value.length !== 1 ? 's' : ''}`;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(ids) {
        this.localSelectedIds = Array.isArray(ids) ? [...ids] : [];
      },
    },

    groupUsers(users) {
      users.forEach((user) => {
        const id = user.userID || user.uid;
        if (id) this.$set(this.usersCache, id, user);
      });
    },

    searchText(val) {
      this._debounceSearch(val);
    },

    groupId: {
      immediate: true,
      handler(id) {
        if (id) this.loadPage(1, '');
      },
    },
  },

  created() {
    this._debounceSearch = (() => {
      let t;
      return (v) => {
        clearTimeout(t);
        t = setTimeout(() => {
          if (v.length === 0 || v.length >= 3) {
            this.loadPage(1, v);
          }
        }, 700);
      };
    })();
  },

  methods: {
    ...mapActions({
      loadGroupUsers: 'loadGroupUsers',
    }),

    openModal() {
      if (this.disabled) return;
      this.localSelectedIds = Array.isArray(this.value) ? [...this.value] : [];
      this.dialog = true;
      this.searchText = '';
      this.currentPage = 1;
      this.loadPage(1, '');
    },

    closeModal() {
      this.dialog = false;
      this.searchText = '';
      this.currentPage = 1;
    },

    async loadPage(page, search = this.searchText) {
      if (!this.groupId) return;
      const requestId = ++this.currentSearchRequestId;
      this.loading = true;
      this.currentPage = page;
      try {
        const args = { groupID: this.groupId, page };
        if (search) args.search = search;
        await this.loadGroupUsers(args);
      } finally {
        if (requestId === this.currentSearchRequestId) {
          this.loading = false;
        }
      }
    },

    onUserClick(user) {
      const id = user.userID || user.uid;
      if (!id) return;
      this.$set(this.usersCache, id, user);
      const idx = this.localSelectedIds.indexOf(id);

      if (this.singleSelect) {
        this.localSelectedIds = idx > -1 ? [] : [id];
        return;
      }

      if (idx > -1) {
        this.localSelectedIds.splice(idx, 1);
      } else {
        this.localSelectedIds.push(id);
      }
    },

    isSelected(userID) {
      return this.localSelectedIds.includes(userID);
    },

    confirmSelection() {
      const selectedIds = this.localSelectedIds.filter(
        (id) => !this.excludedUserIds.map(String).includes(String(id))
      );
      const selectedUsers = selectedIds.map((id) => this.usersCache[id]).filter(Boolean);
      this.$emit('input', [...selectedIds]);
      this.$emit('sosUserSelectChanged', [...selectedIds], selectedUsers);
      this.closeModal();
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.loadPage(this.currentPage - 1);
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.loadPage(this.currentPage + 1);
      }
    },

    setPage(page) {
      this.loadPage(parseInt(page));
    },
  },
};
</script>

<style lang="scss" scoped>
.searchBlock {
  position: relative;
  display: block;
  width: 100%;
  height: 45px;
  box-sizing: border-box;
}

.searchField {
  width: 100%;
  height: 100%;
  padding: 0 20px 0 40px;
  outline: 0;
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-bottom-width: 2px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: #ffffff;
  font-size: 15px;
  box-sizing: border-box;

  &:disabled {
    background-color: #cccccc;
  }
}

.searchFieldIcon {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 12px;
  margin: auto 0 !important;
  height: fit-content;
  z-index: 10;
}
</style>
